import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { formatCurrency } from 'tio-reimagined/format';
import Button from 'tio-reimagined/components/button';
import { eq } from 'tio-ui/utilities';
import {
  getCurrentDemoClientData,
  getCurrentDemoClientId,
} from 'tio-employee/utils/career-pathways-demo-data';
import {
  PhRobot,
  PhPaperPlaneRight,
  PhSparkle,
  PhUser,
  PhShieldCheck,
  PhChartLine,
  PhBank,
  PhScales,
  PhCalculator,
  PhHandshake,
  PhInfo,
  PhArrowRight,
  PhBookOpen,
} from 'ember-phosphor-icons';
import type CareerPathwaysApiService from 'tio-employee/services/career-pathways-api';

interface LoanAdvisorSignature {
  Args: Record<string, never>;
}

interface ChatMessage {
  id: number;
  role: 'assistant' | 'user';
  content: string;
  timestamp: string;
  actions?: ChatAction[];
}

interface ChatAction {
  label: string;
  type: 'primary' | 'secondary' | 'link';
  action?: string;
}

interface LoanContext {
  totalBalance: number;
  monthlyPayment: number;
  interestRate: number;
  loanCount: number;
  pslfEligible: boolean;
  employerContribution: number;
  lrapPrograms: number;
  yearsInService: number;
}

// Demo loan data by client
const LOAN_CONTEXT_BY_CLIENT: Record<string, LoanContext> = {
  advocate: {
    totalBalance: 68420,
    monthlyPayment: 485,
    interestRate: 5.8,
    loanCount: 3,
    pslfEligible: true,
    employerContribution: 2000,
    lrapPrograms: 2,
    yearsInService: 5,
  },
  ups: {
    totalBalance: 34500,
    monthlyPayment: 320,
    interestRate: 4.5,
    loanCount: 2,
    pslfEligible: false,
    employerContribution: 5250,
    lrapPrograms: 0,
    yearsInService: 3,
  },
  starbucks: {
    totalBalance: 45000,
    monthlyPayment: 410,
    interestRate: 6.2,
    loanCount: 4,
    pslfEligible: false,
    employerContribution: 1000,
    lrapPrograms: 1,
    yearsInService: 2,
  },
};

const buildWelcomeMessage = (context: LoanContext): ChatMessage => {
  const clientData = getCurrentDemoClientData();
  const firstName = clientData.employee?.firstName || 'there';

  let greeting = `Hi ${firstName}! 👋 I'm your AI Loan Advisor. I specialize in helping you navigate student loan repayment, maximize forgiveness opportunities, and optimize your benefits.`;

  if (context.pslfEligible) {
    greeting += `\n\nI notice you work for a **qualifying employer** and may be eligible for **Public Service Loan Forgiveness (PSLF)**. With ${context.yearsInService} years of service, you're potentially ${context.yearsInService * 12} payments toward the 120 required for forgiveness!`;
  }

  if (context.lrapPrograms > 0) {
    greeting += `\n\nI also found **${context.lrapPrograms} LRAP programs** you may qualify for that could add to your employer's $${context.employerContribution.toLocaleString()}/year contribution.`;
  }

  greeting += '\n\nWhat would you like to explore today?';

  return {
    id: 1,
    role: 'assistant',
    content: greeting,
    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  };
};

// AI responses by topic and client
const AI_RESPONSES: Record<string, Record<string, string>> = {
  advocate: {
    pslf: `Great question about **PSLF**! Based on your profile:

📋 **Your PSLF Status:**
- ✅ Qualifying employer (501(c)(3) healthcare)
- ✅ Federal Direct Loans (eligible)
- ⏳ ~60 qualifying payments made (estimated)
- 📅 ~60 more payments until forgiveness

**Estimated Forgiveness:** ~$58,000

💡 **My Recommendations:**
1. **File your ECF now** - Verify your payment counts with the Department of Education
2. **Enroll in SAVE** - This IDR plan often gives the lowest payment for PSLF participants
3. **Don't refinance!** - This would permanently disqualify your loans from PSLF

Would you like me to help you understand the ECF process or calculate your optimal IDR payment?`,

    idr: `Let's find your best **Income-Driven Repayment (IDR)** plan! Here's my analysis:

📊 **IDR Plan Comparison for Your Situation:**

| Plan | Monthly Payment | Total Paid (10yr) | Forgiveness |
|------|----------------|-------------------|-------------|
| **SAVE** | ~$285 | $34,200 | $55,000+ |
| PAYE | ~$380 | $45,600 | $40,000+ |
| IBR | ~$410 | $49,200 | $35,000+ |

🏆 **Winner: SAVE Plan**
- Lowest payment at your income level
- Interest subsidy prevents balance growth
- PSLF-compatible (essential for you!)

⚠️ **Important:** The SAVE plan was recently challenged in court. I recommend:
1. Apply for SAVE now while it's available
2. Have PAYE as a backup plan
3. Monitor regulatory updates

Want me to walk you through the SAVE application or explain how payments are calculated?`,

    refinance: `⚠️ **Critical Warning About Refinancing:**

Based on your profile, **refinancing would be a costly mistake**. Here's why:

❌ **What You'd Lose:**
- PSLF eligibility (~$58,000 in potential forgiveness)
- IDR plan access (capped payments based on income)
- Federal protections (forbearance, deferment)

**The Math:**
- Refinancing might save: ~$3,000 in interest
- PSLF forgiveness potential: ~$58,000
- **Net loss: ~$55,000**

✅ **What You Should Do Instead:**
1. Stay on your federal loans
2. Use SAVE for lowest payments
3. Continue toward PSLF

I know refinance ads are tempting, but for your situation, federal loan benefits are far more valuable. Would you like me to explain more about why PSLF is your best path?`,

    strategy: `Based on your complete financial picture, here's my recommended **Optimal Repayment Strategy**:

🎯 **Primary Strategy: PSLF + IDR Optimization**

**Monthly Payment Plan:**
- Current standard payment: $485
- Recommended (SAVE plan): $285
- **Monthly savings: $200**

**How to maximize:**
1. **IDR Certification** - Recertify income annually to keep payments low
2. **ECF Filing** - Submit every 6-12 months to track qualifying payments
3. **Employer Contribution** - Apply $166/mo from Advocate toward principal (optional)

**10-Year Projection:**
- Payments made: $34,200
- Employer contribution: $20,000
- Amount forgiven: ~$58,000
- **Total out-of-pocket: ~$14,200**

Compare to standard repayment: $68,420 + interest = ~$85,000

**Your savings: ~$70,800** 🎉

Would you like a detailed month-by-month projection or help getting started with any of these steps?`,

    lrap: `Great news! You may qualify for additional **Loan Repayment Assistance Programs (LRAP)**:

🎓 **Programs Found:**

**1. Illinois HRSA State Loan Repayment Program**
- Award: Up to $50,000
- Requirement: 2-year service commitment in underserved area
- Status: Your hospital may qualify as a designated site

**2. School-Specific LRAP (if applicable)**
- Many nursing schools offer alumni LRAPs
- Check with your alma mater

⚠️ **Stacking Considerations:**
- Your employer provides $2,000/year already
- Some programs have "outside funds" policies
- PSLF counts regardless of who makes payments

**My Recommendation:**
Research the Illinois HRSA program. Combined with PSLF, you could reduce your effective debt to near zero over 10 years.

Want me to help you understand how these programs stack with your current benefits?`,
  },
  ups: {
    pslf: `Based on your employment at UPS, **PSLF is not available** - UPS is a for-profit company.

However, you have excellent alternatives:

✅ **Your Best Options:**

**1. Employer Match Program**
- UPS contributes up to $5,250/year toward your loans
- This is free money - make sure you're enrolled!

**2. Standard Repayment Acceleration**
- With employer contributions, you can pay off faster
- No need to wait 10+ years for forgiveness

**3. IDR if Needed**
- If payments are difficult, IDR plans can help
- 20-25 year forgiveness (with tax implications)

📊 **Your Projection:**
- Balance: $34,500
- With UPS contributions: Paid off in ~5 years
- Without: ~10 years on standard

Would you like help optimizing how to apply your employer contributions?`,

    idr: `Let's evaluate **IDR plans** for your situation at UPS:

📊 **Your IDR Options:**

Since PSLF isn't available (for-profit employer), the calculation is different:

| Plan | Monthly Payment | Forgiveness Timeline |
|------|----------------|---------------------|
| SAVE | ~$220 | 20-25 years |
| PAYE | ~$280 | 20 years |
| Standard | ~$320 | 10 years |

💡 **My Analysis:**

Given your UPS tuition benefit of $5,250/year, I actually recommend **staying on standard repayment**:

1. Your employer contribution covers 16+ months of payments/year
2. You'll be debt-free in ~5 years (vs 20+ on IDR)
3. No forgiveness tax bomb

**Better Strategy:**
- Keep standard payment
- Apply employer contribution to principal
- Be debt-free by 2029

Would you like a detailed payoff projection?`,

    refinance: `For your situation, **refinancing could make sense** - here's my analysis:

✅ **Refinancing Pros:**
- You're not PSLF-eligible (for-profit employer)
- Could lower rate from 4.5% to ~3.5%
- Save ~$2,000 over loan term

⚠️ **Refinancing Cons:**
- Lose federal protections (forbearance, etc.)
- Fixed rate vs. potentially variable
- Can't go back to federal

🤔 **My Recommendation:**

Actually, I'd suggest **not refinancing** because:
1. Your UPS benefit ($5,250/yr) accelerates payoff significantly
2. At your current pace, loans paid off in ~5 years
3. Interest savings from refinance (~$2,000) are minimal
4. Federal protections have value

**Better approach:** Keep federal loans, maximize employer benefit, and be debt-free faster than refinancing would help anyway.

Want me to show you the comparison math?`,

    strategy: `Here's your **Optimal Repayment Strategy** at UPS:

🎯 **Primary Strategy: Accelerated Payoff with Employer Match**

**Monthly Payment Plan:**
- Your payment: $320/month
- UPS contribution: ~$437/month ($5,250/yr)
- **Total monthly toward debt: $757**

**Payoff Timeline:**
- Without employer help: 10 years
- With UPS benefit: **~4.5 years** 🎉

**Action Items:**
1. ✅ Confirm you're enrolled in UPS Learn & Earn
2. ✅ Keep federal loans (don't refinance)
3. ✅ Apply all employer funds to highest-rate loan first

**Your Projection:**
| Year | Balance | Employer Paid | You Paid |
|------|---------|---------------|----------|
| 2025 | $34,500 | $0 | $0 |
| 2026 | $25,410 | $5,250 | $3,840 |
| 2027 | $16,320 | $5,250 | $3,840 |
| 2028 | $7,230 | $5,250 | $3,840 |
| 2029 | $0 | $3,390 | $3,840 |

**Total paid: $15,360 (you) + $19,140 (UPS)**

Would you like help optimizing which loans to target first?`,

    lrap: `For employees at **for-profit companies** like UPS, traditional LRAP programs aren't typically available. But you have great options:

✅ **What You Have Access To:**

**1. UPS Learn & Earn Program** 💰
- Up to $5,250/year toward student loans
- This IS your LRAP equivalent!
- No service commitment required

**2. Potential State Programs**
- Some states offer loan assistance regardless of employer type
- Check your state's higher education website

**3. Professional/Field-Specific Programs**
- If you have specialized education, check professional associations

💡 **My Recommendation:**
Your UPS benefit is actually better than many LRAP programs because:
- No service commitment
- No application/competition
- Guaranteed annual amount

Maximize this benefit - it's worth $26,250+ over 5 years!

Would you like help ensuring you're getting the full UPS benefit?`,
  },
  starbucks: {
    pslf: `As a Starbucks partner, **PSLF is not available** since Starbucks is a for-profit company.

But you have the **Starbucks College Achievement Plan** which is incredibly valuable!

✅ **Your Best Options:**

**1. SCAP Tuition Coverage**
- Full tuition coverage for ASU Online
- Can reduce/eliminate future borrowing

**2. Existing Loan Repayment**
- Starbucks provides up to $1,000/year toward existing loans
- Make sure you're enrolled!

**3. IDR with 20-Year Forgiveness**
- Available but has tax implications
- Generally not recommended given your balance

📊 **My Recommendation:**
Focus on:
1. Use SCAP to avoid new debt
2. Apply employer $1,000/year to existing loans
3. Pay extra when possible to accelerate payoff

Would you like help creating a payoff plan?`,

    idr: `Let's look at **IDR plans** for your Starbucks situation:

📊 **Your IDR Analysis:**

| Plan | Monthly Payment | Total Paid | Forgiveness |
|------|----------------|-----------|-------------|
| SAVE | ~$310 | $74,400 (20yr) | ~$15,000 |
| Standard | ~$410 | $49,200 (10yr) | $0 |

🤔 **The Trade-off:**

**IDR (SAVE) Path:**
- Lower monthly payments
- Pay $25,000 MORE over time
- Forgiveness is taxable (~$3,000+ tax bill)

**Standard Path:**
- Higher monthly payments
- Debt-free in 10 years
- No forgiveness tax

💡 **My Recommendation:**
Given your balance ($45,000), standard repayment with the Starbucks $1,000/year benefit is probably better:
- Paid off in ~9 years
- No forgiveness tax bomb
- Total savings vs IDR: ~$28,000

Would you like a detailed comparison?`,

    refinance: `Let's evaluate **refinancing** for your situation:

📊 **Analysis:**

**Current Federal Loans:**
- Rate: 6.2%
- Payment: $410/month
- Protection: Federal benefits

**Potential Refinance:**
- Rate: ~5.0% (estimated)
- Payment: ~$380/month
- Protection: None

🤔 **Should You Refinance?**

**Arguments For:**
- ~$30/month savings
- ~$3,600 savings over loan life

**Arguments Against:**
- Lose federal protections
- Lose income-driven options
- Lose potential future forgiveness programs

💡 **My Recommendation:**
At $3,600 total savings, the risk isn't worth it. Federal protections are valuable insurance.

**Better Strategy:**
1. Keep federal loans
2. Use Starbucks $1,000/year benefit
3. Make extra payments when possible
4. Debt-free in ~8 years

Want me to show alternative acceleration strategies?`,

    strategy: `Here's your **Optimal Repayment Strategy** as a Starbucks partner:

🎯 **Primary Strategy: Accelerated Payoff + SCAP**

**For Existing Loans:**
- Standard repayment (keep federal benefits)
- Apply Starbucks $1,000/year to principal
- Target highest-interest loan first

**Monthly Plan:**
- Your payment: $410
- Starbucks contribution: ~$83/month

**Payoff Projection:**
- Without help: 10 years
- With Starbucks benefit: **~8.5 years**

**For Future Education:**
Use SCAP to complete your degree debt-free!
- Full tuition coverage at ASU Online
- No new student loans needed

**Action Items:**
1. ✅ Confirm enrollment in Student Loan Management benefit
2. ✅ Consider SCAP for degree completion
3. ✅ Apply any raises/extra income to loans

Want help prioritizing which loans to pay first?`,

    lrap: `As a **Starbucks partner**, here are your loan assistance options:

💚 **Starbucks Programs:**

**1. Student Loan Management Program**
- Up to $1,000/year toward existing loans
- Enroll through partner benefits portal

**2. Starbucks College Achievement Plan (SCAP)**
- Full tuition coverage for ASU Online
- Prevents future debt accumulation

**3. Emergency Financial Assistance**
- CUP Fund for partner hardships
- Can help with loan payments during emergencies

🎓 **External Programs:**

Based on your situation, you might also qualify for:
- State-based loan assistance programs
- Field-specific LRAP (depending on your degree)

💡 **My Recommendation:**
1. **Enroll now** in Student Loan Management ($1,000/yr)
2. **Consider SCAP** for additional education
3. Research your state's loan assistance programs

The combination of SCAP + Student Loan Management is valuable - equivalent to ~$6,000+ per year if you're studying!

Would you like help exploring any of these options?`,
  },
};

export default class LoanAdvisorIndex extends Component<LoanAdvisorSignature> {
  @service('career-pathways-api') declare careerPathwaysApi: CareerPathwaysApiService;

  @tracked messages: ChatMessage[] = [buildWelcomeMessage(this.loanContext)];
  @tracked inputValue = '';
  @tracked isTyping = false;
  @tracked showActionPanel = false;

  get clientId() {
    return getCurrentDemoClientId();
  }

  get loanContext(): LoanContext {
    return LOAN_CONTEXT_BY_CLIENT[this.clientId] ?? LOAN_CONTEXT_BY_CLIENT.advocate!;
  }

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get suggestedPrompts() {
    const prompts = [
      {
        icon: PhShieldCheck,
        text: 'Am I eligible for PSLF?',
        category: 'pslf',
        color: 'blue',
      },
      {
        icon: PhCalculator,
        text: 'What IDR plan is best for me?',
        category: 'idr',
        color: 'violet',
      },
      {
        icon: PhScales,
        text: 'Should I refinance my loans?',
        category: 'refinance',
        color: 'amber',
      },
      {
        icon: PhChartLine,
        text: "What's my optimal repayment strategy?",
        category: 'strategy',
        color: 'emerald',
      },
    ];

    // Add LRAP prompt if applicable
    if (this.loanContext.lrapPrograms > 0 || this.loanContext.pslfEligible) {
      prompts.push({
        icon: PhHandshake,
        text: 'What LRAP programs can I access?',
        category: 'lrap',
        color: 'purple',
      });
    }

    return prompts;
  }

  get aiResponses(): Record<string, string> {
    return AI_RESPONSES[this.clientId] ?? AI_RESPONSES.advocate!;
  }

  sendMessage = () => {
    if (!this.inputValue.trim()) return;

    const userMessage: ChatMessage = {
      id: this.messages.length + 1,
      role: 'user',
      content: this.inputValue,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    this.messages = [...this.messages, userMessage];
    const query = this.inputValue.toLowerCase();
    this.inputValue = '';
    this.isTyping = true;

    // Simulate AI thinking
    setTimeout(() => {
      let response = this.aiResponses.strategy ?? ''; // default

      if (
        query.includes('pslf') ||
        query.includes('public service') ||
        query.includes('forgiveness') ||
        query.includes('eligible')
      ) {
        response = this.aiResponses.pslf ?? '';
      } else if (
        query.includes('idr') ||
        query.includes('income') ||
        query.includes('driven') ||
        query.includes('save plan') ||
        query.includes('paye') ||
        query.includes('ibr')
      ) {
        response = this.aiResponses.idr ?? '';
      } else if (
        query.includes('refinanc') ||
        query.includes('private') ||
        query.includes('rate')
      ) {
        response = this.aiResponses.refinance ?? '';
      } else if (
        query.includes('lrap') ||
        query.includes('repayment assistance') ||
        query.includes('program')
      ) {
        response = this.aiResponses.lrap ?? '';
      } else if (
        query.includes('strategy') ||
        query.includes('optimal') ||
        query.includes('best') ||
        query.includes('should i')
      ) {
        response = this.aiResponses.strategy ?? '';
      }

      const aiMessage: ChatMessage = {
        id: this.messages.length + 1,
        role: 'assistant',
        content:
          response ||
          "I'd be happy to help with that! Could you tell me more about what you're trying to understand?",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };

      this.messages = [...this.messages, aiMessage];
      this.isTyping = false;
    }, 1500);
  };

  handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  };

  useSuggestedPrompt = (text: string) => {
    this.inputValue = text;
    this.sendMessage();
  };

  updateInput = (event: Event) => {
    this.inputValue = (event.target as HTMLTextAreaElement).value;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8">
      <div class="w-full max-w-6xl mx-auto px-4">
        <div class="flex flex-col lg:flex-row gap-6 h-[calc(100vh-180px)]">
          {{! Main Chat Area }}
          <div class="flex-1 flex flex-col bg-white rounded-2xl shadow-lg overflow-hidden">
            {{! Chat Header }}
            <div class="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 text-white p-5">
              <div class="flex items-center gap-4">
                <div class="p-3 bg-white/20 rounded-xl backdrop-blur">
                  <PhRobot @size={{32}} />
                </div>
                <div class="flex-1">
                  <Heading @level={{2}} class="text-white text-xl font-bold">
                    AI Loan Advisor
                  </Heading>
                  <Text class="text-blue-200 text-sm">
                    Expert guidance for student loan repayment • Personalized to your situation
                  </Text>
                </div>
                <div class="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5">
                  <span class="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                  <Text class="text-blue-100 text-sm">Online</Text>
                </div>
              </div>

              {{! Quick Stats }}
              <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div class="bg-white/10 rounded-lg p-3 backdrop-blur">
                  <Text class="text-blue-200 text-xs">Total Balance</Text>
                  <Text class="text-white font-bold">
                    {{formatCurrency this.loanContext.totalBalance}}
                  </Text>
                </div>
                <div class="bg-white/10 rounded-lg p-3 backdrop-blur">
                  <Text class="text-blue-200 text-xs">Monthly Payment</Text>
                  <Text class="text-white font-bold">
                    {{formatCurrency this.loanContext.monthlyPayment}}
                  </Text>
                </div>
                <div class="bg-white/10 rounded-lg p-3 backdrop-blur">
                  <Text class="text-blue-200 text-xs">Employer Help</Text>
                  <Text class="text-white font-bold">
                    {{formatCurrency this.loanContext.employerContribution}}/yr
                  </Text>
                </div>
                <div class="bg-white/10 rounded-lg p-3 backdrop-blur">
                  <Text class="text-blue-200 text-xs">PSLF Status</Text>
                  <Text class="text-white font-bold">
                    {{#if this.loanContext.pslfEligible}}
                      Eligible ✓
                    {{else}}
                      Not Eligible
                    {{/if}}
                  </Text>
                </div>
              </div>
            </div>

            {{! Chat Messages }}
            <div class="flex-1 overflow-y-auto p-6 space-y-6" id="chat-messages">
              {{#each this.messages as |message|}}
                <div class="flex gap-4 {{if (eq message.role 'user') 'flex-row-reverse'}}">
                  {{#if (eq message.role "assistant")}}
                    <div
                      class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0"
                    >
                      <PhRobot @size={{20}} class="text-white" />
                    </div>
                  {{else}}
                    <div
                      class="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center shrink-0"
                    >
                      <PhUser @size={{20}} class="text-white" />
                    </div>
                  {{/if}}

                  <div class="flex-1 max-w-[80%] {{if (eq message.role 'user') 'text-right'}}">
                    <div
                      class="inline-block p-4 rounded-2xl prose prose-sm max-w-none whitespace-pre-wrap
                        {{if
                          (eq message.role 'user')
                          'bg-blue-600 text-white rounded-br-md'
                          'tio-bg-sunken tio-fg-primary rounded-bl-md'
                        }}"
                    >
                      {{htmlSafe (this.formatMarkdown message.content)}}
                    </div>
                    <Text
                      class="text-xs tio-fg-disabled mt-1
                        {{if (eq message.role 'user') 'text-right'}}"
                    >
                      {{message.timestamp}}
                    </Text>
                  </div>
                </div>
              {{/each}}

              {{#if this.isTyping}}
                <div class="flex gap-4">
                  <div
                    class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center"
                  >
                    <PhRobot @size={{20}} class="text-white" />
                  </div>
                  <div class="tio-bg-sunken rounded-2xl rounded-bl-md p-4">
                    <div class="flex gap-1.5">
                      <span
                        class="w-2 h-2 bg-gray-400 rounded-full animate-bounce animation-delay-0"
                      ></span>
                      <span
                        class="w-2 h-2 bg-gray-400 rounded-full animate-bounce animation-delay-100"
                      ></span>
                      <span
                        class="w-2 h-2 bg-gray-400 rounded-full animate-bounce animation-delay-200"
                      ></span>
                    </div>
                  </div>
                </div>
              {{/if}}
            </div>

            {{! Suggested Prompts }}
            {{#if (eq this.messages.length 1)}}
              <div class="px-6 pb-4">
                <Text class="text-sm tio-fg-muted mb-3">Suggested questions:</Text>
                <div class="flex flex-wrap gap-2">
                  {{#each this.suggestedPrompts as |prompt|}}
                    <button
                      type="button"
                      class="flex items-center gap-2 px-4 py-2 rounded-full border tio-border-default hover:border-blue-300 hover:bg-blue-100 transition-all text-sm"
                      {{on "click" (fn this.useSuggestedPrompt prompt.text)}}
                    >
                      <prompt.icon
                        @size={{16}}
                        class={{if
                          (eq prompt.color "blue")
                          "tio-fg-info"
                          (if
                            (eq prompt.color "violet")
                            "text-violet-500"
                            (if
                              (eq prompt.color "amber")
                              "tio-fg-warning"
                              (if (eq prompt.color "emerald") "tio-fg-success" "text-purple-500")
                            )
                          )
                        }}
                      />
                      <span class="tio-fg-secondary">{{prompt.text}}</span>
                    </button>
                  {{/each}}
                </div>
              </div>
            {{/if}}

            {{! Input Area }}
            <div class="border-t tio-border-default p-4 tio-bg-sunken">
              <div class="flex items-end gap-3">
                <div class="flex-1 relative">
                  <textarea
                    placeholder="Ask about PSLF, IDR plans, refinancing, or your repayment strategy..."
                    class="w-full px-4 py-3 pr-12 border tio-border-strong rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows="2"
                    value={{this.inputValue}}
                    {{on "input" this.updateInput}}
                    {{on "keypress" this.handleKeyPress}}
                  ></textarea>
                </div>
                <Button
                  @intent="primary"
                  class="rounded-xl px-6 py-3"
                  disabled={{this.isTyping}}
                  {{on "click" this.sendMessage}}
                >
                  <PhPaperPlaneRight @size={{20}} />
                </Button>
              </div>
              <Text class="text-xs tio-fg-disabled mt-2 text-center">
                AI-powered guidance based on your loan profile. Always verify with official sources.
              </Text>
            </div>
          </div>

          {{! Sidebar - Quick Actions & Resources }}
          <div class="w-full lg:w-80 space-y-4">
            {{! Loan Summary Card }}
            <div class="bg-white rounded-xl shadow-sm p-5">
              <div class="flex items-center gap-2 mb-4">
                <PhBank @size={{20}} class="tio-fg-info" />
                <Heading @level={{3}} class="tio-fg-primary font-semibold">Your Loans</Heading>
              </div>
              <div class="space-y-3">
                <div class="flex justify-between items-center">
                  <Text class="tio-fg-secondary">Total Balance</Text>
                  <Text class="font-bold tio-fg-primary">
                    {{formatCurrency this.loanContext.totalBalance}}
                  </Text>
                </div>
                <div class="flex justify-between items-center">
                  <Text class="tio-fg-secondary">Loan Count</Text>
                  <Text class="font-semibold tio-fg-secondary">
                    {{this.loanContext.loanCount}}
                    loans
                  </Text>
                </div>
                <div class="flex justify-between items-center">
                  <Text class="tio-fg-secondary">Avg Rate</Text>
                  <Text class="font-semibold tio-fg-secondary">
                    {{this.loanContext.interestRate}}%
                  </Text>
                </div>
                <div class="h-px bg-gray-200 my-2"></div>
                <div class="flex justify-between items-center">
                  <Text class="tio-fg-secondary">Monthly Payment</Text>
                  <Text class="font-bold tio-fg-info">
                    {{formatCurrency this.loanContext.monthlyPayment}}
                  </Text>
                </div>
              </div>
            </div>

            {{! PSLF Status Card }}
            {{#if this.loanContext.pslfEligible}}
              <div
                class="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 border tio-border-success"
              >
                <div class="flex items-center gap-2 mb-3">
                  <PhShieldCheck @size={{20}} class="tio-fg-success" />
                  <Heading @level={{3}} class="tio-fg-success-h font-semibold">
                    PSLF Progress
                  </Heading>
                </div>
                <div class="space-y-3">
                  <div>
                    <div class="flex justify-between text-sm mb-1">
                      <Text class="tio-fg-success-body">Qualifying Payments</Text>
                      <Text class="font-semibold tio-fg-success-h">
                        ~{{this.estimatedPayments}}/120
                      </Text>
                    </div>
                    <div class="w-full h-3 bg-emerald-200 rounded-full overflow-hidden">
                      <div
                        class="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all"
                        style={{this.pslfProgressStyle}}
                      ></div>
                    </div>
                  </div>
                  <div class="flex justify-between items-center text-sm">
                    <Text class="tio-fg-success-body">Est. Forgiveness</Text>
                    <Text class="font-bold tio-fg-success-h">~$58,000</Text>
                  </div>
                </div>
              </div>
            {{/if}}

            {{! Employer Benefits Card }}
            <div class="bg-white rounded-xl shadow-sm p-5">
              <div class="flex items-center gap-2 mb-4">
                <PhHandshake @size={{20}} class="tio-fg-brand" />
                <Heading @level={{3}} class="tio-fg-primary font-semibold">Employer Benefits</Heading>
              </div>
              <div class="space-y-3">
                <div class="p-3 tio-bg-brand-wash rounded-lg">
                  <Text class="text-sm tio-fg-brand">Annual Contribution</Text>
                  <Text class="text-xl font-bold tio-fg-brand">
                    {{formatCurrency this.loanContext.employerContribution}}/year
                  </Text>
                </div>
                {{#if this.loanContext.lrapPrograms}}
                  <div class="flex items-center gap-2 text-sm">
                    <PhSparkle @size={{16}} class="tio-fg-warning" />
                    <Text class="tio-fg-secondary">
                      +{{this.loanContext.lrapPrograms}}
                      LRAP programs available
                    </Text>
                  </div>
                {{/if}}
              </div>
            </div>

            {{! Quick Links }}
            <div class="bg-white rounded-xl shadow-sm p-5">
              <div class="flex items-center gap-2 mb-4">
                <PhBookOpen @size={{20}} class="tio-fg-secondary" />
                <Heading @level={{3}} class="tio-fg-primary font-semibold">Resources</Heading>
              </div>
              <div class="space-y-2">
                <a
                  href="https://studentaid.gov/pslf/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-2 p-2 rounded-lg hover:tio-bg-sunken transition-colors"
                >
                  <PhArrowRight @size={{16}} class="tio-fg-disabled" />
                  <Text class="text-sm tio-fg-info hover:underline">PSLF Help Tool</Text>
                </a>
                <a
                  href="https://studentaid.gov/loan-simulator/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-2 p-2 rounded-lg hover:tio-bg-sunken transition-colors"
                >
                  <PhArrowRight @size={{16}} class="tio-fg-disabled" />
                  <Text class="text-sm tio-fg-info hover:underline">Loan Simulator</Text>
                </a>
                <a
                  href="https://studentaid.gov/manage-loans/repayment/plans"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-2 p-2 rounded-lg hover:tio-bg-sunken transition-colors"
                >
                  <PhArrowRight @size={{16}} class="tio-fg-disabled" />
                  <Text class="text-sm tio-fg-info hover:underline">Repayment Plans</Text>
                </a>
              </div>
            </div>

            {{! Disclaimer }}
            <div class="p-4 tio-bg-warning-soft rounded-xl border tio-border-warning">
              <div class="flex items-start gap-2">
                <PhInfo @size={{16}} class="tio-fg-warning shrink-0 mt-0.5" />
                <Text class="text-xs tio-fg-warning-h">
                  This AI provides general guidance based on your loan profile. For official
                  decisions, consult studentaid.gov or a qualified financial advisor.
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </VStack>
  </template>

  // Helper to format markdown-like content
  formatMarkdown = (content: string) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br>')
      .replace(/\|(.*?)\|/g, '<span class="font-mono">$1</span>');
  };

  get estimatedPayments() {
    return this.loanContext.yearsInService * 12;
  }

  get pslfProgressPercent() {
    return Math.round((this.estimatedPayments / 120) * 100);
  }

  get pslfProgressStyle() {
    return htmlSafe(`width: ${this.pslfProgressPercent}%`);
  }
}
